import React from 'react';

const HIDE_DELAY_MS = 1200;
const SCROLLING_CLASS = 'is-scrolling';

const isScrollable = (el: HTMLElement) =>
  el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

/**
 * Adds/removes `.is-scrolling` on scrollable elements to create a
 * show-on-scroll / show-on-hover / hide-on-idle scrollbar effect.
 * Pair with CSS that toggles `scrollbar-color` on `.is-scrolling`.
 */
const useAutoHideScrollbars = () => {
  React.useEffect(() => {
    const timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
    const hovered = new WeakSet<HTMLElement>();

    const clearTimer = (el: HTMLElement) => {
      const t = timers.get(el);
      if (t) {
        clearTimeout(t);
        timers.delete(el);
      }
    };

    const hide = (el: HTMLElement) => {
      clearTimer(el);
      timers.set(
        el,
        setTimeout(() => {
          el.classList.remove(SCROLLING_CLASS);
          timers.delete(el);
        }, HIDE_DELAY_MS),
      );
    };

    const show = (el: HTMLElement) => {
      clearTimer(el);
      el.classList.add(SCROLLING_CLASS);
    };

    const onScroll = (e: Event) => {
      // Page-level scroll fires on the Document, not an HTMLElement
      const target = e.target instanceof Document ? e.target.documentElement : e.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      show(target);
      if (!hovered.has(target)) {
        hide(target);
      }
    };

    const onEnter = (e: Event) => {
      if (!(e.target instanceof HTMLElement) || !isScrollable(e.target)) {
        return;
      }
      hovered.add(e.target);
      show(e.target);
    };

    const onLeave = (e: Event) => {
      if (!(e.target instanceof HTMLElement) || !hovered.delete(e.target)) {
        return;
      }
      hide(e.target);
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('mouseenter', onEnter, true);
    document.addEventListener('mouseleave', onLeave, true);
    return () => {
      document.removeEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      } as EventListenerOptions);
      document.removeEventListener('mouseenter', onEnter, true);
      document.removeEventListener('mouseleave', onLeave, true);
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);
};

export default useAutoHideScrollbars;
